import { createAsyncThunk } from "@reduxjs/toolkit";
import api from "Service/api/api";
import type { RootState } from "Redux/store";
import {
  INotificationsParams,
  NOTIFICATIONS_PRIORITY,
} from "Shared/Types/notifications";
import {
  hideAllNotificationsState,
  markNotificationRead,
  deleteAllNotificationsState,
} from "./slice";
import { IReducerWithCallback } from "Shared/Types/shared";

type NotificationRequest = INotificationsParams & { forceRefresh?: boolean };
const fetchNotifications = (request: NotificationRequest | null) => {
  const { forceRefresh, ...params } = request || {};
  return forceRefresh
    ? api.notifications.getNotificationsList(params, { force: true })
    : api.notifications.getNotificationsList(params);
};

const getNotifications = createAsyncThunk(
  "notifications/getNotifications",
  async (params: NotificationRequest | null) => {
    try {
      const response = await fetchNotifications(params);
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  {
    condition: (params, { getState }) => {
      const state = (getState() as RootState).notificationSlice;
      return (
        !state.loading ||
        state.activeView !== (params?.is_read === false ? "unread" : "all")
      );
    },
  },
);

const getNotificationsMore = createAsyncThunk(
  "notifications/getNotificationsMore",
  async (params: NotificationRequest | null) => {
    try {
      const response = await fetchNotifications(params);
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  {
    condition: (params, { getState }) => {
      const state = (getState() as RootState).notificationSlice;
      return (
        !state.loading ||
        state.activeView !== (params?.is_read === false ? "unread" : "all")
      );
    },
  },
);

const checkNotificationsCount = createAsyncThunk(
  "notifications/checkNotificationsCount",
  async (params: NotificationRequest | null) => {
    try {
      const response = await fetchNotifications({
        ...(params || {}),
        is_read: false,
        limit: 1,
        offset: 0,
      });

      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  {
    condition: (_params, { getState }) =>
      !(getState() as RootState).notificationSlice.countLoading,
  },
);

const checkUrgentNotifications = createAsyncThunk(
  "notifications/checkUrgentNotifications",
  async (params: NotificationRequest | null) => {
    try {
      const responses = await Promise.all(
        [NOTIFICATIONS_PRIORITY.URGENT, NOTIFICATIONS_PRIORITY.HIGH].map(
          (priority) =>
            fetchNotifications({
              ...(params || {}),
              priority,
              is_read: false,
              limit: 1,
              offset: 0,
            }),
        ),
      );
      return {
        ...responses[0].data,
        count: responses.reduce(
          (count, response) => count + response.data.count,
          0,
        ),
      };
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  {
    condition: (_params, { getState }) =>
      !(getState() as RootState).notificationSlice.priorityLoading,
  },
);

const markAsRead = createAsyncThunk(
  "notifications/markAsRead",
  async ({ data, onSuccess, onError }: IReducerWithCallback<string>, state) => {
    try {
      await api.notifications.markAsRead(data);
      state.dispatch(markNotificationRead(data));
      state.dispatch(checkNotificationsCount({ forceRefresh: true }));
      state.dispatch(checkUrgentNotifications({ forceRefresh: true }));
      if (onSuccess) onSuccess();
    } catch (error) {
      if (onError) onError();
      console.error(error);
      throw error;
    }
  },
);

const markAllAsRead = createAsyncThunk(
  "notifications/markAllAsRead",
  async (_p, state) => {
    try {
      await api.notifications.markAllReadNotifications();
      state.dispatch(hideAllNotificationsState());
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
);

const deleteAllNotifications = createAsyncThunk(
  "notifications/deleteAllNotifications",
  async (_p, state) => {
    try {
      await api.notifications.hideAllNotifications();
      state.dispatch(deleteAllNotificationsState());
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
);

export {
  getNotifications,
  checkNotificationsCount,
  checkUrgentNotifications,
  markAsRead,
  markAllAsRead,
  deleteAllNotifications,
  getNotificationsMore,
};
